import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, FileText, CheckCircle2, XCircle, Eye } from "lucide-react";
import { AssignmentOrganizer } from "@/components/organizer/AssignmentOrganizer";
import { DashboardSkeleton, LoadError } from "@/components/PageState";
import { SubmissionStatusBadge } from "@/components/SubmissionStatusBadge";

const SECTIONS = [
  { key: "overview", label: "Overview" },
  { key: "assignments", label: "Assignments" },
  { key: "worksheets", label: "Worksheets" },
  { key: "lessons", label: "Lessons" },
  { key: "passages", label: "Passages" },
  { key: "reflections", label: "Reflections" },
  { key: "journal", label: "Journal" },
];

export default function ParentStudent() {
  const { studentId } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");

  const load = useCallback(async () => {
    try {
      const [full, subs, ach, submissionData, lessonData] = await Promise.all([
        api.get(`/parent/student/${studentId}/full`),
        api.get("/curriculum/subjects"),
        api.get(`/achievements?student_id=${studentId}`),
        api.get(`/submissions?student_id=${studentId}`),
        api.get("/curriculum/lessons"),
      ]);
      setData({ ...full.data, submissions: submissionData.data }); setSubjects(subs.data); setAchievements(ach.data); setLessons(lessonData.data); setError("");
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Student report could not load.");
    }
  }, [studentId]);
  useEffect(() => { load(); }, [load, retry]);

  if (error) return <LoadError message={error} onRetry={()=>{setError("");setRetry((value)=>value+1);}} testId="parent-student-load-error" />;
  if (!data) return <DashboardSkeleton testId="parent-student-loading" cards={4} />;
  const { student, assignments, submissions, metrics, by_subject, worksheets, progress, passage_submissions, reflections, journal } = data;
  const assignmentMap = Object.fromEntries(assignments.map((assignment) => [assignment.id, assignment]));
  const lessonMap = Object.fromEntries(lessons.map((lesson) => [lesson.id, lesson]));
  const assignmentReflections = (submissions || []).filter((submission) => submission.reflection?.trim()).map((submission) => ({
    ...submission,
    assignment_title: assignmentMap[submission.assignment_id]?.title || "Assignment",
    source_date: submission.submitted_at || submission.draft_saved_at || submission.updated_at,
  }));
  const requestedAssignmentId = searchParams.get("assignment");

  return (
    <div className="space-y-6" data-testid="parent-student-page">
      <Link to="/parent" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-teal-700"><ArrowLeft size={14} /> Back</Link>

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="chip mb-2">Student report</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">{student.name}</h1>
          <div className="text-slate-500 text-sm">{student.email} · {student.points} points · {achievements.length} badges</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/parent/student/${student.id}/view`} data-testid="view-as-student" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 bg-white text-sm font-semibold"><Eye size={14} /> View student dashboard</Link>
          <Link to={`/report/${student.id}`} data-testid="view-report-cta" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold"><FileText size={14} /> Report card</Link>
          <Link to={`/portfolio?student_id=${student.id}`} data-testid="view-portfolio-cta" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-slate-200 text-sm font-semibold">Portfolio</Link>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 rounded-full bg-slate-100 p-1 w-fit">
        {SECTIONS.map((s) => (
          <button key={s.key} data-testid={`ps-tab-${s.key}`} onClick={()=>setTab(s.key)}
            className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold ${tab === s.key ? "bg-white text-teal-800 shadow-sm" : "text-slate-500"}`}>{s.label}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-5">
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="student-summary-metrics">
            <SummaryMetric label="Curriculum lessons completed" value={`${metrics.curriculum_lessons_completed}/${metrics.curriculum_lessons_total}`} testId="student-report-curriculum-lessons" />
            <SummaryMetric label="Assignments turned in" value={metrics.assignments_turned_in} testId="student-report-assignments-turned-in" />
            <SummaryMetric label="Assignments graded" value={metrics.assignments_graded} testId="student-report-assignments-graded" />
            <SummaryMetric label="Assignment records" value={metrics.assignments_total} testId="student-report-assignment-count" />
          </section>
          <section className="grid sm:grid-cols-2 gap-4" data-testid="skill-progress-grid">
          {subjects.map((s) => {
            const summary = by_subject[s.slug] || { lessons_done: 0, lessons_total: s.lesson_count || 0 };
            const total = summary.lessons_total;
            const done = summary.lessons_done;
            return (
              <div key={s.slug} className="p-5 rounded-2xl bg-white border border-slate-200">
                <div className="flex justify-between items-baseline">
                  <div className="font-display font-semibold text-slate-900">{s.title}</div>
                  <div className="text-sm text-slate-500">{done} / {total} curriculum lessons</div>
                </div>
                <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-teal-600" style={{ width: `${Math.min(100, (done/total)*100)}%` }} />
                </div>
              </div>
            );
          })}
          </section>
        </div>
      )}

      {tab === "assignments" && (
        <div data-testid="ps-assignments">
          <AssignmentOrganizer scope={`student-${student.id}`} students={[student]} subjects={subjects} studentId={student.id} focusId={requestedAssignmentId} onMutated={load} />
        </div>
      )}
      {tab === "worksheets" && (
        <div className="space-y-3" data-testid="ps-worksheets">
          {worksheets.map((w) => (
            <details key={w.id} className="p-4 rounded-2xl bg-white border border-slate-200" data-testid={`ps-worksheet-${w.id}`}>
              <summary className="flex items-center justify-between cursor-pointer">
                <div>
                  <div className="font-semibold text-slate-900">{w.title}</div>
                  <div className="text-xs text-slate-500">{w.subject} · {w.items.length} items · <span className="uppercase tracking-widest">{w.status}</span></div>
                </div>
                {w.status === "graded" && <span className="chip">{w.score}/{w.max_score}</span>}
              </summary>
              <div className="mt-3 space-y-2 text-sm">
                {w.items.map((it, i) => {
                  const d = w.detail?.[it.id];
                  const ans = w.submitted_answers?.[it.id];
                  return (
                    <div key={it.id} className={`p-3 rounded-xl border ${d ? (d.correct ? "bg-teal-50 border-teal-200" : "bg-red-50 border-red-200") : "bg-white border-slate-200"}`}>
                      <div className="text-xs text-slate-500 uppercase">{it.type}</div>
                      <div className="font-medium text-slate-900">{i+1}. {it.prompt}</div>
                      {it.type === "mc" && it.choices && (
                        <div className="mt-1 text-xs text-slate-600">
                          Choices: {it.choices.map((c, ci) => <span key={ci} className={ci === Number(it.answer) ? "text-teal-800 font-semibold" : ""}>{c}{ci < it.choices.length - 1 ? " · " : ""}</span>)}
                          <div>Answered: <b>{ans != null ? it.choices[Number(ans)] : "—"}</b></div>
                        </div>
                      )}
                      {it.type === "fill" && (
                        <div className="mt-1 text-xs text-slate-600">Expected: <b>{it.answer}</b> · Answered: <b>{ans || "—"}</b></div>
                      )}
                      {d && (d.correct ? <div className="mt-1 text-teal-700 text-xs font-semibold flex items-center gap-1"><CheckCircle2 size={12} /> Correct</div> : <div className="mt-1 text-red-700 text-xs font-semibold flex items-center gap-1"><XCircle size={12} /> Not quite</div>)}
                    </div>
                  );
                })}
              </div>
            </details>
          ))}
          {worksheets.length === 0 && <div className="text-slate-500 text-sm">No worksheets yet.</div>}
        </div>
      )}

      {tab === "lessons" && (
        <div className="space-y-2" data-testid="ps-lessons">
          {progress.filter((p)=>p.status==="completed").map((p) => (
            <div key={p.lesson_id + p.updated_at} className="p-3 rounded-xl bg-white border border-slate-200 flex justify-between">
              <div>
                <div className="font-semibold text-slate-900">{p.lesson_id}</div>
                <div className="text-xs text-slate-500">{p.subject} · {new Date(p.updated_at).toLocaleDateString()}</div>
              </div>
              {p.score != null && <span className="chip">{p.score}%</span>}
            </div>
          ))}
          {progress.filter((p)=>p.status==="completed").length === 0 && <div className="text-slate-500 text-sm">No lessons completed yet.</div>}
        </div>
      )}

      {tab === "passages" && (
        <div className="space-y-2" data-testid="ps-passages">
          {passage_submissions.map((p) => (
            <div key={p.id} className="p-3 rounded-xl bg-white border border-slate-200 flex justify-between">
              <div>
                <div className="font-semibold text-slate-900">{p.passage_id}</div>
                <div className="text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString()}</div>
              </div>
              <span className="chip">{p.pct}% ({p.score}/{p.total})</span>
            </div>
          ))}
          {passage_submissions.length === 0 && <div className="text-slate-500 text-sm">No passage attempts yet.</div>}
        </div>
      )}

      {tab === "reflections" && (
        <div className="space-y-6" data-testid="ps-reflections">
          <section data-testid="assignment-reflections-section">
            <h2 className="font-display text-lg font-semibold text-slate-900">Assignment reflections</h2>
            <p className="text-xs text-slate-500 mt-1 mb-3">Reflections saved with teacher-created assignment submissions.</p>
            <div className="space-y-2">
              {assignmentReflections.map((reflection) => (
                <div key={reflection.assignment_id} data-testid={`assignment-reflection-${reflection.assignment_id}`} className="p-4 rounded-2xl bg-sky-50 border border-sky-100">
                  <div className="flex flex-wrap items-center justify-between gap-2"><div className="font-semibold text-slate-900">{reflection.assignment_title}</div><SubmissionStatusBadge status={reflection.status} testId={`assignment-reflection-status-${reflection.assignment_id}`} /></div>
                  <div className="text-xs text-slate-500 mt-1">Assignment · {reflection.source_date ? new Date(reflection.source_date).toLocaleDateString() : "Date unavailable"}</div>
                  <div className="mt-2 whitespace-pre-wrap text-slate-800">{reflection.reflection}</div>
                </div>
              ))}
              {assignmentReflections.length === 0 && <div className="text-slate-500 text-sm">No assignment reflections yet.</div>}
            </div>
          </section>
          <section data-testid="standalone-reflections-section">
            <h2 className="font-display text-lg font-semibold text-slate-900">Standalone lesson reflections</h2>
            <p className="text-xs text-slate-500 mt-1 mb-3">Reflection forms completed after curriculum lessons.</p>
            <div className="space-y-2">
              {reflections.map((r) => (
                <div key={r.id} data-testid={`standalone-reflection-${r.id}`} className="p-4 rounded-2xl bg-white border border-slate-200">
                  <div className="text-xs text-slate-500">Lesson · {lessonMap[r.lesson_id]?.title || r.lesson_id} · {new Date(r.created_at).toLocaleDateString()} · Completed</div>
                  <div className="text-slate-900 mt-1"><b>Learned:</b> {r.learned}</div>
                  {r.how_learned && <div className="text-slate-700 text-sm"><b>How:</b> {r.how_learned}</div>}
                  {r.next_focus && <div className="text-slate-700 text-sm"><b>Next:</b> {r.next_focus}</div>}
                </div>
              ))}
              {reflections.length === 0 && <div className="text-slate-500 text-sm">No standalone lesson reflections yet.</div>}
            </div>
          </section>
        </div>
      )}

      {tab === "journal" && (
        <div className="space-y-3" data-testid="ps-journal">
          {journal.map((j) => (
            <div key={j.id} className="p-4 rounded-2xl bg-white border border-slate-200">
              <div className="font-semibold text-slate-900">{j.title || "Untitled"}</div>
              <div className="text-xs text-slate-500">{new Date(j.created_at).toLocaleDateString()}</div>
              <div className="mt-2 whitespace-pre-wrap text-slate-800 text-sm">{j.text}</div>
            </div>
          ))}
          {journal.length === 0 && <div className="text-slate-500 text-sm">No journal entries yet.</div>}
        </div>
      )}
    </div>
  );
}

function SummaryMetric({ label, value, testId }) {
  return <div className="p-4 rounded-2xl bg-white border border-slate-200"><div className="text-xs uppercase tracking-widest text-slate-500">{label}</div><div data-testid={testId} className="mt-1 font-display text-2xl font-bold text-slate-900">{value}</div></div>;
}
